import React from "react";

import { Table, Column } from "@tanstack/react-table";

import {
    FilterMode,
    FilterValueWithMode,
    hasException,
    isSameModeFamily,
    isStringMode,
    oppositeMode,
} from "../../../domain/table/filterValueWithMode";
import { FilterBarRow } from "../../FilterBar";
import { columnPlainLabel } from "../TSTTable/utils/columnPlainLabel";

import { TSTTextFilterInput } from "./TSTTextFilterInput";
import { useTSTFilterUtils } from "./useTSTFilterUtils";

export interface ModeConfig {
    options: { label: string; value: FilterMode }[];
    /** Used for a fresh filter, or when the persisted mode isn't offered. */
    defaultMode: FilterMode;
    textFilterPlaceholder?: string;
}

export interface ExceptionConfig {
    addLabel: string;
    /**
     * The set-mode value editor for the exception. String modes share the
     * row's text input instead.
     */
    renderComponent: () => React.ReactNode;
}

interface TSTFilterBarRowProps<T> {
    column: Column<T>;
    children: React.ReactNode;
    table: Table<any>;
    /** Optional mode config for the operator dropdown */
    modeConfig?: ModeConfig;
    /**
     * Lets the row take an exception: a second value matched with the
     * opposite operator. Needs `modeConfig` to offer that operator.
     */
    exceptionConfig?: ExceptionConfig;
}

export function TSTFilterBarRow<T>({
    column,
    children,
    table,
    modeConfig,
    exceptionConfig,
}: TSTFilterBarRowProps<T>) {
    const { filterableColumns, selectedFilterColumnIds } =
        useTSTFilterUtils(table);

    const columnOptions = filterableColumns.map((col) => ({
        label: columnPlainLabel(col, table),

        // We may allow multiple filters on the same column in the future for AND logic
        disabled: selectedFilterColumnIds.includes(col.id),
        value: col.id,
    }));

    const handleFilterChange = (newFilterColId: string, column: Column<T>) => {
        const currentFilters = table.getState().columnFilters;
        const updatedFilters = currentFilters
            .filter((filter) => filter.id !== column.id) // Remove old filter
            .concat({ id: newFilterColId, value: null }); // Add new filter

        table.setColumnFilters(updatedFilters);
    };

    // Get current mode from filter value (assumes { value, mode } structure)
    const filterValue = column.getFilterValue() as
        | FilterValueWithMode<unknown>
        | null
        | undefined;
    // If no modeConfig, always use "is" mode
    const offeredMode = modeConfig?.options.find(
        (option) => option.value === filterValue?.mode
    )?.value;
    const currentMode: FilterMode = modeConfig
        ? (offeredMode ?? modeConfig.defaultMode)
        : "is";

    const exceptionMode = oppositeMode(currentMode);
    const exceptionModeOption = modeConfig?.options.find(
        (option) => option.value === exceptionMode
    );
    const rowHasException = hasException(filterValue);

    // Rebuilds the filter value around the current mode, keeping the parts
    // not being changed.
    const updateFilterValue = (
        changes: Partial<FilterValueWithMode<unknown>>
    ) => {
        column.setFilterValue({
            value: filterValue?.value ?? null,
            mode: currentMode,
            ...(rowHasException ? { exception: filterValue.exception } : {}),
            ...changes,
        });
    };

    const handleModeChange = (newModeRaw: string) => {
        const newMode = newModeRaw as FilterMode;
        const nextValue = isSameModeFamily(currentMode, newMode)
            ? (filterValue?.value ?? null)
            : null;
        column.setFilterValue({ value: nextValue, mode: newMode });
    };

    const textToValue = (next: string) => (next.trim() === "" ? null : next);

    const handleTextChange = (next: string) => {
        updateFilterValue({ value: textToValue(next) });
    };

    const handleExceptionTextChange = (next: string) => {
        updateFilterValue({ exception: textToValue(next) });
    };

    const handleAddException = () => {
        updateFilterValue({ exception: null });
    };

    // The first click removes the exception, the next one the whole filter.
    const handleRemove = () => {
        if (rowHasException) {
            column.setFilterValue({
                value: filterValue.value,
                mode: currentMode,
            });
        } else {
            column.setFilterValue(undefined);
        }
    };

    // String modes share one text input; set modes use the column-specific
    // filter component passed as children.
    const renderFilterInput = () => {
        if (isStringMode(currentMode)) {
            return (
                <TSTTextFilterInput
                    value={
                        typeof filterValue?.value === "string"
                            ? filterValue.value
                            : ""
                    }
                    onChange={handleTextChange}
                    placeholder={modeConfig?.textFilterPlaceholder}
                />
            );
        }
        return children;
    };

    const renderExceptionInput = () => {
        if (isStringMode(exceptionMode)) {
            return (
                <TSTTextFilterInput
                    value={
                        rowHasException &&
                        typeof filterValue.exception === "string"
                            ? filterValue.exception
                            : ""
                    }
                    onChange={handleExceptionTextChange}
                    placeholder={modeConfig?.textFilterPlaceholder}
                />
            );
        }
        return exceptionConfig?.renderComponent();
    };

    const canAddException =
        exceptionConfig !== undefined && exceptionModeOption !== undefined;

    return (
        <FilterBarRow
            key={column.id}
            columnOptions={columnOptions}
            onColumnOptionChange={(value) => handleFilterChange(value, column)}
            onFilterRemoveClick={handleRemove}
            selectedColumnOption={column.id}
            modeOptions={modeConfig?.options}
            selectedMode={currentMode}
            onModeChange={modeConfig ? handleModeChange : undefined}
            onAddExceptionClick={
                canAddException ? handleAddException : undefined
            }
            addExceptionLabel={exceptionConfig?.addLabel}
            exception={
                rowHasException
                    ? {
                          modeLabel:
                              exceptionModeOption?.label ?? exceptionMode,
                          children: renderExceptionInput(),
                      }
                    : undefined
            }
        >
            {renderFilterInput()}
        </FilterBarRow>
    );
}
